import React from 'react';
import styled from 'styled-components';
import { hexToRgba } from '@mrkt/features/color-conversion';
import {
  screenSmMax,
  screenXsMax,
  screenSmMin,
  spacer4,
  spacer8,
  spacer12,
  spacer16,
  spacer32,
  spacer40,
  spacer56,
} from '@spotify-internal/encore-foundation';
import { IconArrowRight, Type } from '@spotify-internal/encore-web';
import { formatText, fixHref } from '../../../utils';
import {
  ButtonTertiaryAsLink,
  withAnimationContext,
  DELAYS,
} from '../../../ui';
import { RelatedLinkType } from './RelatedLinkType';
import { ModuleType } from '../../index';
import { fadeInAnimation } from '../../../ui/Animations';

const RelatedLinkBox = styled.div`
  @media (max-width: ${screenXsMax}) {
    padding: ${spacer32} ${spacer12};
  }

  @media (min-width: ${screenSmMin}) {
    padding: ${spacer40} ${spacer40};
  }
`;

const Headline = withAnimationContext(
  styled(Type.h1).attrs({
    variant: 'heading3',
  })`
    ${fadeInAnimation(DELAYS.FIRST)};
    padding-bottom: 0px;
  `,
);

const Subtext = withAnimationContext(
  styled(Type.p).attrs({
    variant: 'body1',
  })`
    ${fadeInAnimation(DELAYS.SECOND)};
    width: 80%;

    @media (max-width: ${screenSmMax}) {
      width: 100%;
    }
  `,
);

const Description = withAnimationContext(
  styled(Type.p).attrs({
    variant: 'body2',
  })`
    ${fadeInAnimation(DELAYS.THIRD)};
  `,
);

const TextContainer = styled.div`
  display: flex;
  flex-direction: row;
  flex: 1;
  padding-bottom: ${spacer56};

  @media (max-width: ${screenSmMax}) {
    flex-direction: column;
    padding-bottom: ${spacer8};
  }
`;

const LinkBox = styled.div`
  @media (max-width: ${screenSmMax}) {
    padding-top: 0px;
  }
`;

const InnerTextContainer = styled.div`
  text-align: right;
  display: flex;
  flex-direction: column;
  width: 80%;
  align-items: flex-end;

  @media (max-width: ${screenSmMax}) {
    align-items: flex-start;
    text-align: left;
    padding-bottom: ${spacer16};
  }
`;

const RelatedTextLink = withAnimationContext(
  styled(ButtonTertiaryAsLink)`
    ${fadeInAnimation(DELAYS.FOURTH)};
    padding: 0;
    color: ${({ theme }) => theme.box.text};

    &:hover:not(:focus):not(:disabled) {
      transform: none;
      color: ${({ theme }) => hexToRgba(theme.box.text, 0.25)};
    }

    svg {
      padding-left: ${spacer8};
      padding-bottom: ${spacer4};
      vertical-align: middle;
    }
  `,
);

const TRACK_CAT = 'module-relatedLinksModule';

export type RelatedLinkModuleType = {
  relatedLink: RelatedLinkType;
};

export const RelatedLink = React.forwardRef<
  React.MutableRefObject<ModuleType>,
  RelatedLinkModuleType
>(({ relatedLink = {} }, ref) => {
  return (
    <RelatedLinkBox ref={ref as React.RefObject<HTMLDivElement>}>
      <TextContainer>
        <Headline>{relatedLink.headline}</Headline>
        <InnerTextContainer>
          <Subtext
            dangerouslySetInnerHTML={{
              __html: formatText(relatedLink.subtext),
            }}
          />
          <Description>{relatedLink.description}</Description>
        </InnerTextContainer>
      </TextContainer>
      <LinkBox>
        <RelatedTextLink
          href={fixHref(relatedLink.link)}
          track={[TRACK_CAT, `Link - ${relatedLink.linkText}`]}
        >
          {relatedLink.linkText}
          <IconArrowRight />
        </RelatedTextLink>
      </LinkBox>
    </RelatedLinkBox>
  );
});
